/**
 * LegalView — PadelPulse public legal sheets (Privacy Policy, Terms of Service)
 *
 * Both legal pages share one legible, prose-first layout. The only difference
 * between them is content, so each page is described by a small config object
 * (`LEGAL_PAGES`) of i18n keys and rendered by a common `LegalView` base. This
 * keeps the sheets config-driven and fully localized (en/es) without a CMS.
 */

import { t, getLocale, type TranslationKey } from "../i18n/config";
import { renderPublicHeader, renderPublicFooter, setupPublicChrome, type PublicNav } from "./publicChrome";

/** One section of a legal document: a heading and a body paragraph. */
interface LegalSection {
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
}

interface LegalPageConfig {
  nav: PublicNav;
  titleKey: TranslationKey;
  introKey: TranslationKey;
  sections: LegalSection[];
  contactTitleKey: TranslationKey;
  contactBodyKey: TranslationKey;
}

const PRIVACY: LegalPageConfig = {
  nav: "privacy",
  titleKey: "privacy.title",
  introKey: "privacy.intro",
  sections: [
    { titleKey: "privacy.s1.title", bodyKey: "privacy.s1.body" },
    { titleKey: "privacy.s2.title", bodyKey: "privacy.s2.body" },
    { titleKey: "privacy.s3.title", bodyKey: "privacy.s3.body" },
    { titleKey: "privacy.s4.title", bodyKey: "privacy.s4.body" },
    { titleKey: "privacy.s5.title", bodyKey: "privacy.s5.body" },
  ],
  contactTitleKey: "privacy.contact.title",
  contactBodyKey: "privacy.contact.body",
};

const TERMS: LegalPageConfig = {
  nav: "terms",
  titleKey: "terms.title",
  introKey: "terms.intro",
  sections: [
    { titleKey: "terms.s1.title", bodyKey: "terms.s1.body" },
    { titleKey: "terms.s2.title", bodyKey: "terms.s2.body" },
    { titleKey: "terms.s3.title", bodyKey: "terms.s3.body" },
    { titleKey: "terms.s4.title", bodyKey: "terms.s4.body" },
    { titleKey: "terms.s5.title", bodyKey: "terms.s5.body" },
  ],
  contactTitleKey: "terms.contact.title",
  contactBodyKey: "terms.contact.body",
};

/**
 * Static "last reviewed" date for the legal sheets. Formatted per active locale
 * so the "Last updated" line reads naturally in both English and Spanish.
 */
const LAST_UPDATED = new Date("2026-07-01T00:00:00Z");

class LegalView {
  private container: HTMLElement;
  private config: LegalPageConfig;

  constructor(container: HTMLElement, config: LegalPageConfig) {
    this.container = container;
    this.config = config;
  }

  async render(): Promise<void> {
    const { config } = this;

    const formattedDate = LAST_UPDATED.toLocaleDateString(getLocale(), {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const sections = config.sections
      .map(
        (section) => `
          <section class="space-y-3">
            <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t(section.titleKey)}</h2>
            <p class="text-base leading-relaxed text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${t(section.bodyKey)}</p>
          </section>`
      )
      .join("");

    this.container.innerHTML = `
      <div class="min-h-screen bg-background-light dark:bg-background-dark daylight:bg-day-canvas flex flex-col">
        ${renderPublicHeader(config.nav)}

        <main class="flex-1">
          <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
            <a href="#landing" class="inline-flex items-center gap-1 text-sm font-semibold text-primary daylight:text-day-ink hover:text-primary/80 transition-colors mb-8">
              <span class="material-symbols-outlined" style="font-size: 18px;">arrow_back</span>
              <span>${t("legal.backHome")}</span>
            </a>

            <header class="mb-10 space-y-2">
              <h1 class="text-4xl md:text-5xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t(config.titleKey)}</h1>
              <p class="text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("legal.lastUpdated", { date: formattedDate })}</p>
            </header>

            <article class="space-y-8">
              <p class="text-lg leading-relaxed text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${t(config.introKey)}</p>

              ${sections}

              <section class="space-y-3 pt-4 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
                <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t(config.contactTitleKey)}</h2>
                <p class="text-base leading-relaxed text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${t(config.contactBodyKey)}</p>
              </section>
            </article>
          </div>
        </main>

        ${renderPublicFooter()}
      </div>
    `;
    setupPublicChrome(this.container);
  }
}

/** Public `/privacy` (`#privacy`) view. */
export class PrivacyView extends LegalView {
  constructor(container: HTMLElement) {
    super(container, PRIVACY);
  }
}

/** Public `/terms` (`#terms`) view. */
export class TermsView extends LegalView {
  constructor(container: HTMLElement) {
    super(container, TERMS);
  }
}
